import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

/** Mirrors the CHECK on stay_pack_recurrence.frequency. */
export type StayPackFrequency = "daily" | "weekly" | "every_n_days" | "monthly";

export interface StayPackRecurrence {
  frequency: StayPackFrequency;
  intervalDays: number | null;
  /** 0 = Sunday … 6 = Saturday. */
  daysOfWeek: number[];
  /** 1–31. */
  daysOfMonth: number[];
  endsOn: string | null;
}

export interface StayPackMember {
  id: string;
  petId: string;
  petName: string;
  ownerName: string | null;
  addedAt: string;
}

export interface StayPack {
  id: string;
  name: string;
  color: string | null;
  isActive: boolean;
  recurrence: StayPackRecurrence | null;
  members: StayPackMember[];
}

export interface StayPackInput {
  name: string;
  color: string | null;
  recurrence: StayPackRecurrence;
}

export const stayPacksKey = (clinicId: string | null) => ["stay-packs", clinicId];

const DEFAULT_RECURRENCE: StayPackRecurrence = {
  frequency: "weekly",
  intervalDays: null,
  daysOfWeek: [],
  daysOfMonth: [],
  endsOn: null,
};

export const emptyRecurrence = (): StayPackRecurrence => ({ ...DEFAULT_RECURRENCE });

/**
 * Only the columns matching the chosen frequency are persisted; the DDL keeps
 * the others nullable so a weekly pack never carries stale days_of_month.
 */
const recurrenceToRow = (recurrence: StayPackRecurrence) => ({
  frequency: recurrence.frequency,
  interval_days: recurrence.frequency === "every_n_days" ? recurrence.intervalDays : null,
  days_of_week: recurrence.frequency === "weekly" ? recurrence.daysOfWeek : null,
  days_of_month: recurrence.frequency === "monthly" ? recurrence.daysOfMonth : null,
  ends_on: recurrence.endsOn,
});

type RecurrenceRow = {
  frequency: string;
  interval_days: number | null;
  days_of_week: number[] | null;
  days_of_month: number[] | null;
  ends_on: string | null;
};

const rowToRecurrence = (row: RecurrenceRow | null): StayPackRecurrence | null =>
  row
    ? {
        frequency: row.frequency as StayPackFrequency,
        intervalDays: row.interval_days,
        daysOfWeek: row.days_of_week ?? [],
        daysOfMonth: row.days_of_month ?? [],
        endsOn: row.ends_on,
      }
    : null;

/**
 * Packs with their recurrence and *all* members — membership is a standing
 * roster, independent of who has a reservation today.
 */
export function useStayPacks() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: stayPacksKey(clinicId),
    queryFn: async (): Promise<StayPack[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from("stay_packs")
        .select(
          `id, name, color, is_active,
           stay_pack_recurrence ( frequency, interval_days, days_of_week, days_of_month, ends_on ),
           stay_pack_members ( id, pet_id, added_at, pets ( id, name, owners ( name ) ) )`,
        )
        .eq("clinic_id", clinicId)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;

      return (data ?? []).map((pack) => {
        const recurrenceRel = pack.stay_pack_recurrence as RecurrenceRow | RecurrenceRow[] | null;
        const recurrenceRow = Array.isArray(recurrenceRel) ? recurrenceRel[0] ?? null : recurrenceRel;
        const memberRows = (pack.stay_pack_members ?? []) as Array<{
          id: string;
          pet_id: string;
          added_at: string;
          pets: { id: string; name: string; owners: { name: string } | null } | null;
        }>;
        return {
          id: pack.id,
          name: pack.name,
          color: pack.color,
          isActive: pack.is_active,
          recurrence: rowToRecurrence(recurrenceRow),
          members: memberRows
            .map((member) => ({
              id: member.id,
              petId: member.pet_id,
              petName: member.pets?.name ?? "",
              ownerName: member.pets?.owners?.name ?? null,
              addedAt: member.added_at,
            }))
            .sort((a, b) => a.petName.localeCompare(b.petName)),
        };
      });
    },
    enabled: !!clinicId,
  });
}

export function useCreateStayPack() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (input: StayPackInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const { data, error } = await supabase
        .from("stay_packs")
        .insert({ clinic_id: clinicId, name: input.name, color: input.color })
        .select("id")
        .single();
      if (error) throw error;

      const { error: recurrenceError } = await supabase
        .from("stay_pack_recurrence")
        .insert({ stay_pack_id: data.id, ...recurrenceToRow(input.recurrence) });
      if (recurrenceError) throw recurrenceError;
      return data.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPacksKey(clinicId) });
    },
  });
}

export function useUpdateStayPack() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ id, ...input }: StayPackInput & { id: string }) => {
      const { error } = await supabase
        .from("stay_packs")
        .update({ name: input.name, color: input.color })
        .eq("id", id);
      if (error) throw error;

      // stay_pack_id is UNIQUE, so this creates the row for packs that predate
      // a recurrence and overwrites it for those that already have one.
      const { error: recurrenceError } = await supabase
        .from("stay_pack_recurrence")
        .upsert({ stay_pack_id: id, ...recurrenceToRow(input.recurrence) }, { onConflict: "stay_pack_id" });
      if (recurrenceError) throw recurrenceError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPacksKey(clinicId) });
    },
  });
}

/** Soft delete — reservations and activities keep pointing at the pack. */
export function useDeleteStayPack() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("stay_packs").update({ is_active: false }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPacksKey(clinicId) });
    },
  });
}

export function useAddStayPackMember() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async ({ packId, petId }: { packId: string; petId: string }) => {
      const { error } = await supabase
        .from("stay_pack_members")
        .insert({ stay_pack_id: packId, pet_id: petId, added_by: profile?.id ?? null });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPacksKey(clinicId) });
    },
  });
}

export function useRemoveStayPackMember() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase.from("stay_pack_members").delete().eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayPacksKey(clinicId) });
    },
  });
}
